/* eslint-disable no-unused-vars */
// ═══════════════════════════════════════════════════════════════
// FK-5112 TOTAL FLOODING CALCULATION ENGINE v2.1
// Multi-enclosure support — Fixed: cylinder selection, nozzle count,
// reserve agent doubling, strap qty, model display
// ═══════════════════════════════════════════════════════════════

// ─── Unit conversion ───
const FT_TO_M = 0.3048;
const FT3_TO_M3 = 0.0283168466;
export function toMeters(len, isMetric) { return isMetric ? len : len * FT_TO_M; }
export function toCelsius(t, isMetric) { return isMetric ? t : (t - 32) * 5 / 9; }
export function volToM3(vol, isMetric) { return isMetric ? vol : vol * FT3_TO_M3; }

// ─── Altitude correction factor ───
export function altitudeFactor(alt_m) {
  if (alt_m >= -915 && alt_m <= -611) return 1.11;
  if (alt_m >= -610 && alt_m <= -306) return 1.07;
  if (alt_m >= -305 && alt_m <= -1) return 1.04;
  if (alt_m >= 0 && alt_m <= 304) return 1.0;
  if (alt_m >= 305 && alt_m <= 609) return 0.96;
  if (alt_m >= 610 && alt_m <= 914) return 0.93;
  if (alt_m >= 915 && alt_m <= 1218) return 0.89;
  if (alt_m >= 1219 && alt_m <= 1523) return 0.86;
  if (alt_m >= 1524 && alt_m <= 1828) return 0.82;
  if (alt_m >= 1829 && alt_m <= 2133) return 0.78;
  if (alt_m >= 2134 && alt_m <= 2437) return 0.75;
  if (alt_m >= 2438 && alt_m <= 2742) return 0.72;
  if (alt_m >= 2743 && alt_m <= 3047) return 0.69;
  if (alt_m >= 3048 && alt_m <= 3600) return 0.66;
  return 1.0;
}

// ─── Agent mass (returns lbs) ───
export function calculateAgentMass(vol_m3, temp_c, conc_pct, alt_m) {
  const S = 0.0664 + 0.0002741 * temp_c;
  const factor = altitudeFactor(alt_m);
  const mass = ((vol_m3 / S) * (conc_pct / (100 - conc_pct))) * factor + 0.1;
  return Math.ceil(mass * 2.2046);
}

// ─── Single enclosure volume ───
export function enclosureVolume(dims, isMetric) {
  const calc = (l, w, h) => (parseFloat(l) || 0) * (parseFloat(w) || 0) * (parseFloat(h) || 0);
  let total = calc(dims.mainL, dims.mainW, dims.mainH);
  total += calc(dims.floorL, dims.floorW, dims.floorH);
  total += calc(dims.ceilingL, dims.ceilingW, dims.ceilingH);
  return Math.round(total * 100) / 100;
}

// ─── Cylinder fill ranges (max fill in lbs) ───
const CYL_MAX_FILL = {
  "70020": 21, "70035": 38, "70070": 76, "70100": 108,
  "70150": 163, "70250": 271, "70375": 406, "70560": 601,
  "70650": 712, "70800": 900, "71000": 1112, "71200": 1297,
};

// ─── Cylinder selection — picks SMALLEST adequate cylinder ───
export function selectCylinder(agentLbs) {
  if (agentLbs < 11) return { error: "Agent mass below 11 lbs. Consider Micro-Suppression." };
  let n = 1;
  if (agentLbs > 1297) n = Math.ceil(agentLbs / 1297);
  const perCyl = agentLbs / n;

  const ladder = [
    { min: 11, max: 21, model: "CR 70020-E", range: "11 - 21" },
    { min: 22, max: 38, model: "CR 70035-E", range: "18 - 38" },
    { min: 39, max: 76, model: "CR 70070-E", range: "36 - 76" },
    { min: 77, max: 108, model: "CR 70100-F", range: "51 - 108" },
    { min: 109, max: 163, model: "CR 70150-E", range: "77 - 163" },
    { min: 164, max: 271, model: "CR 70250-E", range: "127 - 271" },
    { min: 272, max: 406, model: "CR 70375-E", range: "190 - 406" },
    { min: 407, max: 601, model: "CR 70560-E", range: "281 - 601" },
    { min: 602, max: 712, model: "CR 70650-F", range: "333 - 712" },
    { min: 713, max: 900, model: "CR 70800-F", range: "420 - 900" },
    { min: 901, max: 1112, model: "CR 71000-F", range: "520 - 1112" },
    { min: 1113, max: 1297, model: "CR 71200-E", range: "606 - 1297" },
  ];

  // For multi-cylinder, also check if we can downsize by adding 1 more cylinder
  // E.g., 1400 lbs / 2 = 700 → 800lb cyl. But 1400 / 3 = 467 → 560lb cyl might be cheaper.
  // We pick the config with fewest cylinders that uses the smallest adequate size.

  let match = ladder[ladder.length - 1];
  for (const entry of ladder) {
    if (perCyl >= entry.min && perCyl <= entry.max) { match = entry; break; }
  }

  return { model: match.model, range: match.range, perCyl: Math.round(perCyl), count: n };
}

// ─── Mapping helpers ───
export function mapBracket(modelo, color = "Red") {
  const suffix = color === "Mirror Finish" ? "-MF" : color === "Gold" ? "-GLD" : "";
  const m = modelo.toUpperCase();
  if (m.includes("70020") || m.includes("70035") || m.includes("70070")) return "CR 50139" + suffix;
  if (m.includes("70100") || m.includes("70150")) return "CR 60780" + suffix;
  if (m.includes("70250") || m.includes("70375")) return "CR 60760" + suffix;
  if (m.includes("70560") || m.includes("70650")) return "CR 60770" + suffix;
  if (m.includes("70800") || m.includes("71000") || m.includes("71200")) return "CR 60790" + suffix;
  return "—";
}
export function mapLLI(modelo) {
  const m = modelo.toUpperCase();
  if (m.includes("70150") || m.includes("70250")) return "CR 60020";
  if (m.includes("70375") || m.includes("70560")) return "CR 60020-1";
  if (m.includes("70650")) return "CR 60020-5";
  if (m.includes("70800")) return "CR 60020-4";
  if (m.includes("71000")) return "CR 60020-3";
  if (m.includes("71200")) return "CR 60020-2";
  return "—";
}
export function mapHose(modelo) {
  const m = modelo.toUpperCase();
  if (m.includes("70020") || m.includes("70035") || m.includes("70070") || m.includes("70100")) return "CR 60255";
  if (m.includes("70150") || m.includes("70250")) return "CR 60256";
  if (m.includes("70375") || m.includes("70560")) return "CR 60257";
  if (m.includes("70650") || m.includes("70800") || m.includes("71000")) return "CR 91230-C";
  if (m.includes("71200")) return "CR 91230";
  return "—";
}
export function selectSolenoid(modelo, supervisable) {
  if (supervisable) return { pn: "CR 50026-A + CR 61033-2", desc: "LATCHING SOLENOID WITH SUPERVISION + LOCAL MANUAL CONTROL" };
  if (modelo.toUpperCase().includes("71200")) return { pn: "CR 50025-6", desc: "ELECTRIC SOLENOID 15 WATTS 24 VDC" };
  return { pn: "CR 50025-2", desc: "ELECTRIC SOLENOID 11 WATTS 24 VDC" };
}
export function suggestDetector(detectionType, area) {
  if (detectionType === "Crossed Zone Aspirating") return { pn: "Cupid HM2", desc: "Cupid ASD, 2 Zone, Double Sensor, Release Control - Temp & humidity sensors" };
  if (detectionType === "Traditional Aspirating") {
    if (area <= 0) return { pn: "—", desc: "" };
    if (area <= 250) return { pn: "Cupid HM1", desc: "Cupid ASD, 1 Pipe, 1 Zone - Temp & humidity sensors" };
    if (area < 500) return { pn: "Quartas-500r", desc: "Aspirating Smoke Detector - Multi-zone" };
    return { pn: "Quartas-2000s", desc: "Aspirating Smoke Detector - High sensitivity" };
  }
  if (detectionType === "Passive") return { pn: "SMK DETECT", desc: "Passive smoke detection — Consider aspirating per NFPA 75 & 76" };
  return { pn: "—", desc: "" };
}

function getCylSize(model) {
  const m = model.toUpperCase();
  if (m.includes("71200")) return 1200; if (m.includes("71000")) return 1000;
  if (m.includes("70800")) return 800; if (m.includes("70650")) return 650;
  if (m.includes("70560")) return 560; if (m.includes("70375")) return 375;
  if (m.includes("70250")) return 250; if (m.includes("70150")) return 150;
  if (m.includes("70100")) return 100; if (m.includes("70070")) return 70;
  if (m.includes("70035")) return 35; if (m.includes("70020")) return 20;
  return 0;
}
function getBracketDesc(pn) {
  const base = pn.replace(/-MF|-GLD/g, "");
  const s = pn.includes("-MF") ? " - MIRROR FINISH" : pn.includes("-GLD") ? " - GOLD" : " - RED";
  if (base === "CR 50139") return "20/35/70 LBS CYLINDER STRAP" + s;
  if (base === "CR 60780") return "100/150 LBS CYLINDER STRAP" + s;
  if (base === "CR 60760") return "250/375 LBS CYLINDER STRAP" + s;
  if (base === "CR 60770") return "560/650 LBS CYLINDER STRAP" + s;
  if (base === "CR 60790") return "800/1,000/1,200 LBS CYLINDER STRAP" + s;
  return "CYLINDER STRAP";
}
function getLLIDesc(pn) {
  if (pn === "CR 60020") return "LIQUID LEVEL INDICATOR 150 / 250 LB. CYLINDER";
  if (pn === "CR 60020-1") return "LIQUID LEVEL INDICATOR 375 / 560 LB. CYLINDER";
  if (pn === "CR 60020-5") return "LIQUID LEVEL INDICATOR 650 LB. CYLINDER";
  if (pn === "CR 60020-4") return "LIQUID LEVEL INDICATOR 800 LB. CYLINDER";
  if (pn === "CR 60020-3") return "LIQUID LEVEL INDICATOR 1,000 LB. CYLINDER";
  if (pn === "CR 60020-2") return "LIQUID LEVEL INDICATOR 1,200 LB. CYLINDER";
  return "LIQUID LEVEL INDICATOR";
}
function getHoseDesc(pn) {
  if (pn === "CR 60255") return "1 in. NPT FLEXIBLE CYLINDER HOSE";
  if (pn === "CR 60256") return "1 - 1/2 in. NPT FLEXIBLE CYLINDER HOSE";
  if (pn === "CR 60257") return "2 - 1/2 in. NPT FLEXIBLE CYLINDER METAL HOSE";
  if (pn === "CR 91230-C") return "3 in. NPT SS BRAID FLEXIBLE HOSE";
  if (pn === "CR 91230") return "4 in. NPT SS BRAID FLEXIBLE HOSE";
  return "FLEXIBLE CYLINDER HOSE";
}

// ─── Calculate nozzles for one area ───
// Per Chemori manual 3.8.1: max 1600 ft² (40ft × 40ft) per nozzle, max side 40ft
function calcNozzlesForArea(l, w, h, isMetric) {
  const L = toMeters(parseFloat(l) || 0, isMetric);
  const W = toMeters(parseFloat(w) || 0, isMetric);
  const H = toMeters(parseFloat(h) || 0, isMetric);
  if (L <= 0 || W <= 0 || H <= 0) return 0;
  const maxSide = 12.19; // 40ft in meters — max coverage per nozzle per side
  const nL = Math.ceil(L / maxSide);
  const nW = Math.ceil(W / maxSide);
  const heightFactor = H > 4.88 ? 2 : 1; // >16ft needs 2 tiers
  return nL * nW * heightFactor;
}

// ─── Calculate single enclosure BOM ───
export function calculateTFEnclosureBOM(enclosure, shared, isMetric) {
  const dims = enclosure;
  const vol = enclosureVolume(dims, isMetric);
  if (vol <= 0) return null;

  const alt_m = toMeters(parseFloat(shared.elevation) || 0, isMetric);
  const temp_c = toCelsius(parseFloat(shared.minTemp) || 25, isMetric);
  const conc = parseFloat(shared.concentration) || 4.5;
  const vol_m3 = volToM3(vol, isMetric);
  const agentLbs = calculateAgentMass(vol_m3, temp_c, conc, alt_m);

  const cylResult = selectCylinder(agentLbs);
  if (cylResult.error) return { error: cylResult.error, name: enclosure.name };

  const isReserve = shared.cylinderConfig === "Main & Passive Reserve" || shared.cylinderConfig === "Main & Active Reserve";
  let totalCyl = isReserve ? cylResult.count * 2 : cylResult.count;

  // Selected model is the single model from the ladder (no more slash-separated)
  const selectedModel = cylResult.model.trim();
  const colorSuffix = shared.cylinderColor === "Mirror Finish" ? "-SS-MF" : shared.cylinderColor === "Gold" ? "-SS-GLD" : "-SS";
  const bigCyl = selectedModel.includes("650") || selectedModel.includes("800") || selectedModel.includes("1000") || selectedModel.includes("1200");
  const cylPN = bigCyl ? selectedModel + (shared.cylinderColor === "Mirror Finish" ? "-MF" : shared.cylinderColor === "Gold" ? "-GLD" : "") : selectedModel + colorSuffix;

  const items = [];
  const cylSizeLbs = getCylSize(selectedModel);

  // Agent — doubled for reserve configs (filling both main + reserve cylinders)
  const agentBomQty = isReserve ? agentLbs * 2 : agentLbs;
  items.push({ pn: "CHEMORI FK5112", desc: "FACTORY FILLED FK-5-1-12 CLEAN AGENT (PER LB)", qty: agentBomQty });

  // Cylinder
  items.push({ pn: cylPN, desc: `${cylSizeLbs} LB SS CYLINDER ASSEMBLY W/ SS VALVE`, qty: totalCyl });

  // Bracket — cylinders > 800 lbs need 2 straps per cylinder
  const bracket = mapBracket(selectedModel, shared.cylinderColor);
  const strapsPerCyl = cylSizeLbs > 800 ? 2 : 1;
  if (bracket !== "—") items.push({ pn: bracket, desc: getBracketDesc(bracket), qty: totalCyl * strapsPerCyl });

  // Solenoid
  const sol = selectSolenoid(selectedModel, shared.supervisableSolenoid);
  const solQty = shared.cylinderConfig === "Main & Active Reserve" ? 2 : 1;
  items.push({ pn: sol.pn, desc: sol.desc, qty: solQty });

  // LLI
  if (shared.lli) { const lliPN = mapLLI(selectedModel); if (lliPN !== "—") items.push({ pn: lliPN, desc: getLLIDesc(lliPN), qty: totalCyl }); }

  // Pressure Supervisor
  if (shared.pressureSupervisor) items.push({ pn: "CR 50138-9", desc: "PRESSURE SUPERVISORY SWITCH (N/C) 500PSI & GUARD", qty: totalCyl });

  // Hose
  const hose = mapHose(selectedModel); if (hose !== "—") items.push({ pn: hose, desc: getHoseDesc(hose), qty: totalCyl });

  // Discharge Supervisor
  if (shared.dischargeSupervisor) items.push({ pn: "CR 50339", desc: "PRESSURE SUPERVISORY SWITCH (NC/NO) SPDT - MANUAL RESET", qty: 1 });

  // Nozzles
  let nozzleCount = 0;
  nozzleCount += calcNozzlesForArea(dims.mainL, dims.mainW, dims.mainH, isMetric);
  nozzleCount += calcNozzlesForArea(dims.floorL, dims.floorW, dims.floorH, isMetric);
  nozzleCount += calcNozzlesForArea(dims.ceilingL, dims.ceilingW, dims.ceilingH, isMetric);

  // Add nozzle row to per-enclosure BOM (placeholder drill code — updated after flow check)
  if (nozzleCount > 0) {
    const nDesc = shared.nozzleType === "180° Sidewall" ? "180° SIDEWALL NOZZLE - STAINLESS STEEL" : "360° CENTRAL NOZZLE - STAINLESS STEEL";
    const nPN = shared.nozzleType === "180° Sidewall" ? "CR 80704-2-0.XXXX" : "CR 80704-3-0.XXXX";
    items.push({ pn: nPN, desc: nDesc + " (drill code TBD — run flow check)", qty: nozzleCount });
  }

  // Slaves
  let slaveCount = 0;
  if (cylResult.count > 1) {
    slaveCount = cylResult.count - 1;
    if (shared.cylinderConfig === "Main & Active Reserve") slaveCount *= 2;
  }

  // Detector
  const mainL = toMeters(parseFloat(dims.mainL) || 0, isMetric);
  const mainW = toMeters(parseFloat(dims.mainW) || 0, isMetric);
  const area = mainL > 0 && mainW > 0 ? mainL * mainW : (vol_m3 > 0 ? vol_m3 / 2.7 : 0);
  const detector = suggestDetector(shared.detectionType, area);

  return {
    name: enclosure.name, agentLbs, model: selectedModel, fillRange: cylResult.range,
    cylCount: totalCyl, cylPN, items, nozzleCount, slaveCount, detector, volume: vol,
    mainL: dims.mainL, mainW: dims.mainW, mainH: dims.mainH,
    floorL: dims.floorL, floorW: dims.floorW, floorH: dims.floorH,
    ceilingL: dims.ceilingL, ceilingW: dims.ceilingW, ceilingH: dims.ceilingH,
  };
}

// ─── Multi-enclosure BOM ───
export function generateMultiEnclosureTFBOM(enclosures, shared, isMetric) {
  const results = [];
  let totalAgent = 0;
  let totalNozzles = 0;
  let totalSlaves = 0;
  const aggregated = {};

  for (const enc of enclosures) {
    if (!enc.mainL || !enc.mainW || !enc.mainH) continue;
    const r = calculateTFEnclosureBOM(enc, shared, isMetric);
    if (!r) continue;
    if (r.error) return { error: `${r.name}: ${r.error}` };

    results.push(r);
    totalAgent += r.agentLbs;
    totalNozzles += r.nozzleCount;
    totalSlaves += r.slaveCount;

    for (const item of r.items) {
      if (aggregated[item.pn]) aggregated[item.pn].qty += item.qty;
      else aggregated[item.pn] = { desc: item.desc, qty: item.qty };
    }
  }

  if (results.length === 0) return { error: "No valid enclosures." };

  // Add nozzles to aggregated BOM
  if (totalNozzles > 0) {
    const nDesc = shared.nozzleType === "180° Sidewall" ? "180° SIDEWALL NOZZLE - STAINLESS STEEL" : "360° CENTRAL NOZZLE - STAINLESS STEEL";
    const nPN = shared.nozzleType === "180° Sidewall" ? "CR 80704-2-0.XXXX" : "CR 80704-3-0.XXXX";
    aggregated[nPN] = { desc: nDesc, qty: totalNozzles, highlight: true };
  }

  // Add slaves
  if (totalSlaves > 0) {
    aggregated["CR 61041 + CR 50192-2"] = { desc: "PISTON ACTUATOR SLAVE + 1/4 in. x 36 in. METAL FLEXIBLE HOSE", qty: totalSlaves };
  }

  // Add detector (once for whole project)
  if (results.length > 0) {
    const det = results[0].detector;
    if (det && det.pn !== "—") {
      aggregated[det.pn] = { desc: det.desc, qty: results.length };
    }
  }

  const aggregatedBom = Object.entries(aggregated).map(([pn, v]) => ({ pn, desc: v.desc, qty: v.qty, highlight: v.highlight || false }));

  return { enclosureResults: results, totalAgent, aggregatedBom };
}
